import React from 'react';
import { useCurrencyBalance } from '@/hooks/use-currency';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Coins, Gem, AlertCircle, Plus } from 'lucide-react';
import { useLocation } from 'wouter';

interface CurrencyDisplayProps {
  variant?: 'icon' | 'badge' | 'plain';
  size?: 'sm' | 'md' | 'lg';
  showTopUpButton?: boolean;
}

export function CurrencyDisplay({ 
  variant = 'badge', 
  size = 'md',
  showTopUpButton = false
}: CurrencyDisplayProps) {
  const { data, isLoading, isError } = useCurrencyBalance();
  const [_, setLocation] = useLocation();

  const handleTopUp = () => {
    setLocation('/shop?tab=topup');
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-16 rounded-md bg-gray-700" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 text-red-400">
        <AlertCircle className="h-4 w-4" />
        <span className="text-xs">Balance error</span>
      </div>
    );
  }

  // Data is guaranteed to be valid due to the select function in useCurrencyBalance
  const { currency, premiumCurrency } = data || { currency: 0, premiumCurrency: 0 };

  if (variant === 'icon') {
    return (
      <div className="flex items-center gap-2">
        <div className="bg-amber-900/30 border border-amber-700/60 rounded-md px-2 py-1 flex items-center gap-1">
          <Coins className={`${size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5'} text-amber-400`} />
          <span className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'} font-medium text-white`}>
            {currency}
          </span>
        </div>
        <div className="bg-purple-900/30 border border-purple-700/60 rounded-md px-2 py-1 flex items-center gap-1">
          <Gem className={`${size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5'} text-purple-400`} />
          <span className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'} font-medium text-white`}>
            {premiumCurrency}
          </span>
        </div>
      </div>
    );
  }

  if (variant === 'badge') {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center">
          <Badge variant="outline" className="bg-amber-900/30 text-amber-200 border-amber-700 flex items-center gap-1 py-1">
            <Coins className="h-3 w-3 text-amber-400" />
            <span className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}`}>
              {currency}
            </span>
          </Badge>
        </div>
        <Badge variant="outline" className="bg-purple-900/30 text-purple-200 border-purple-700 flex items-center gap-1 py-1">
          <Gem className="h-3 w-3 text-purple-400" />
          <span className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}`}>
            {premiumCurrency}
          </span>
        </Badge>
      </div>
    );
  }

  // Plain variant
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center">
        <span className="text-gray-400 text-xs mr-1">Coins:</span>
        <span className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'} font-medium text-white`}>
          {currency}
        </span>
      </div>
      <div>
        <span className="text-gray-400 text-xs mr-1">Gems:</span>
        <span className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'} font-medium text-white`}>
          {premiumCurrency}
        </span>
      </div>
    </div>
  );
}